import React, {
  SelectHTMLAttributes,
  useState,
  ReactNode,
  useEffect,
  useRef,
  ChangeEvent
} from 'react';
import cn from '@/utils/cn';
import { useClickOutside } from '@/hooks/useClickOutside';
import TextInput from '@/components/TextInput';
import { CaretDownIcon, DotIcon, ExitIcon } from '@/components/Icons';

const ButtonClassName = cn([
  'bg-base-3',
  'border',
  'border-aux-gray',
  'text-emerge-navy',
  'text-sm',
  'font-medium',
  'rounded-full',
  'block',
  'px-4',
  'py-2',
  'flex',
  'flex-row',
  'justify-between',
  'items-center',
  'gap-x-2',
  'hover:bg-emerge-base-2',
  'active:bg-emerge-base-2',
  'focus:outline-none'
]);

const SelectOptionsClassName = cn([
  'z-10',
  'absolute',
  'mt-10',
  'bg-white',
  'rounded-xl',
  'shadow',
  'w-48',
  'h-auto',
  'overflow-y-auto',
  'max-h-96',
  'vertical-scroll'
]);

export type TSelectOptions = {
  label: string;
  code?: string;
  value: string | undefined;
  color?: string;
};

interface SelectGeneralLedgerCodeProps extends SelectHTMLAttributes<HTMLLIElement> {
  options: Array<TSelectOptions>;
  onOptionSelected: (option: TSelectOptions, optionCode: string) => void;
  onOptionRemoved: () => void;
  header?: ReactNode;
  optionsContainerClass?: string;
  onTextChange: (e: ChangeEvent<HTMLInputElement>) => void;
  search: string;
  selectedColor: string;
  clearSearch: () => void;
  optionSelected: boolean;
}

const SelectGeneralLedgerCode: React.FC<SelectGeneralLedgerCodeProps> = ({
  options,
  onOptionSelected: optionSelectedHandler,
  onOptionRemoved: optionRemovedHandler,
  header,
  optionsContainerClass,
  className,
  value: valueFromParent,
  onTextChange,
  search,
  selectedColor,
  clearSearch,
  optionSelected
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | undefined>(undefined);
  const [showRemove, setShowRemove] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(filterRef, () => {
    setShowOptions(false);
    setShowSearch(false);
    clearSearch();
  });

  const buttonValue = valueFromParent;

  const onOptionRemoved = () => {
    if (optionRemovedHandler && (selectedCode !== undefined || optionSelected)) {
      optionRemovedHandler();
      setShowOptions(false);
      setShowRemove(false);
    }
  };

  const onOptionSelected = (option: TSelectOptions, optionCode: string) => {
    if (optionSelectedHandler) {
      optionSelectedHandler(option, optionCode);
    }
    setSelectedCode(optionCode);
    setShowSearch(false);
    setShowOptions(false);
    clearSearch();
  };

  const handleOptionChange = (label: typeof valueFromParent) => {
    const newOptionCode = options.find((option) => option.label === label);
    setSelectedCode(newOptionCode?.code);

    if (newOptionCode !== undefined || optionSelected) {
      setShowRemove(true);
    }
  };

  useEffect(() => {
    if (valueFromParent) {
      handleOptionChange(valueFromParent);
    }
  }, [buttonValue]);

  return (
    <div ref={filterRef} className="flex justify-end relative">
      <button
        onClick={() => {
          setShowOptions(true);
          setShowSearch(true);
        }}
        className={cn(ButtonClassName, className, showSearch ? 'p-0' : '')}
        type="button"
        value={buttonValue}>
        <span
          className={`text-xs flex items-center overflow-x-hidden whitespace-nowrap text-left w-full ${optionSelected && !selectedCode ? 'text-dark-base-90' : ''}`}>
          {!showSearch ? (
            selectedColor ? (
              <>
                <span className="xl:w-[10%] w-1/4">
                  <DotIcon
                    className="flex mr-3 w-4 h-4"
                    style={{ color: optionSelected && !selectedCode ? '#AAAAA6' : selectedColor }}
                  />
                </span>
                <span className="text-ellipsis overflow-hidden">{buttonValue}</span>
              </>
            ) : (
              buttonValue
            )
          ) : (
            <TextInput
              className="text-xs p-2 border-0"
              autoFocus={true}
              containerClassName="w-full"
              value={search}
              onTextChange={onTextChange!}
              placeholder="Search..."
            />
          )}
        </span>

        {showSearch ? (
          !showOptions ? (
            <span>
              <CaretDownIcon size="xs" className="text-emerge-navy stroke-2" />
            </span>
          ) : null
        ) : (
          <span>
            {showRemove ? (
              <ExitIcon
                onClick={onOptionRemoved}
                className="w-3.5 h-3.5 text-emerge-navy stroke-2 hover:text-emerge-navy-2"
              />
            ) : (
              <CaretDownIcon
                size="xs"
                className={cn('text-emerge-navy stroke-2', showOptions ? 'rotate-180' : '')}
              />
            )}
          </span>
        )}
      </button>

      <div
        className={cn(SelectOptionsClassName, optionsContainerClass, !showOptions ? 'hidden' : '')}>
        {header}
        <ul className="p-2 text-xs text-emerge-navy cursor-pointer">
          {options.length === 0 ? (
            <li>
              <a className="block items-center cursor-default rounded-lg">
                <span>No results</span>
              </a>
            </li>
          ) : (
            options.map((option, optionIndex) => {
              return (
                <li
                  onClick={() =>
                    option.code !== selectedCode ? onOptionSelected(option, option.code!) : null
                  }
                  key={optionIndex}
                  value={option.value}>
                  <a
                    className={`block px-4 items-center py-2${option.code === selectedCode ? ' cursor-default' : ' hover:bg-emerge-teal-3'} rounded-lg`}
                    style={{ wordBreak: 'break-all' }}>
                    {option.color ? (
                      <span className="xl:w-[15%] w-[30%]">
                        <DotIcon className="flex mr-3 w-4 h-4" style={{ color: option.color }} />
                      </span>
                    ) : null}{' '}
                    <span className={option.code === selectedCode ? ' font-semibold' : ''}>
                      {option.label}
                    </span>
                  </a>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

export default SelectGeneralLedgerCode;
